import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon } from 'lucide-react';
import { HubHeader } from '../components/hub/HubHeader';
import { FilterBar } from '../components/hub/FilterBar';
import { DirectoryColumns } from '../components/hub/DirectoryColumns';
import { HubFaqs } from '../components/hub/HubFaqs';
import { SiblingHubs } from '../components/hub/SiblingHubs';
import { TierNote } from '../components/TierNote';
import { DesignNote } from '../components/DesignNote';
import { activityTypes, classesFaqs, classesFilters, classesIntro, classesTier3, openNow, standoutPrograms } from '../data/classes';
const urgencyStyles = {
  now: 'bg-peach text-ink',
  soon: 'bg-foam text-ink',
  waitlist: 'bg-line text-muted'
};
export function ClassesHub() {
  const [active, setActive] = useState<string[]>([]);
  function toggle(option: string) {
    setActive(prev => prev.includes(option) ? prev.filter(x => x !== option) : [...prev, option]);
  }
  const filtered = useMemo(() => {
    if (active.length === 0) return standoutPrograms;
    return standoutPrograms.filter(p => active.every(a => p.tags.includes(a)));
  }, [active]);
  return <main id="main" className="bg-paper" data-id="element-668" data-mp="1fne2dboza6lx">
      <HubHeader crumb="Classes & sports" meta={`${classesIntro.count} · ${classesIntro.updated}`} titleLead="Kids classes & sports in" titleItalic="Santa Barbara" deck={classesIntro.deck} data-id="element-669" data-mp="1foffrmtgv56t" />

      <OpenNowStrip data-id="element-670" data-mp="23bdsqaidnuzz" />

      <section aria-labelledby="types-h" className="bg-paper" data-id="element-671" data-mp="23cyp85o95o0h">
        <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-672" data-mp="1ykxfaqek49dm">
          <TierNote tier="1" label="One page per activity type" note="Different shape from Parks: here Tier 1 is a page per activity, not per business. Nobody searches a studio by name — they search “swim lessons santa barbara”." data-id="element-673" data-mp="1yiudza7yettl" />
          <h2 id="types-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-674" data-mp="2m9gm11hyke3l">
            Start with the activity
          </h2>
          <p className="mt-5 max-w-prose text-[15.5px] leading-[1.8] text-muted" data-id="element-675" data-mp="ogxcnf3rqsg5">
            Each of these is a full page listing every program of that type,
            with prices, ages and the week registration opens.
          </p>

          <ul className="mt-16 grid gap-x-14 gap-y-14 sm:grid-cols-2 lg:grid-cols-3" data-id="element-676" data-mp="pi95j09a53b">
            {activityTypes.map(type => <li key={type.slug} className="group relative" data-id="element-677" data-mp="5p4jnkop978f">
                <span className={`block h-2 w-14 ${type.swatch}`} aria-hidden="true" data-id="element-678" data-mp="3nu7tfipm27f6" />
                <h3 className="mt-6 font-display text-[30px] font-300 leading-tight text-ink" data-id="element-679" data-mp="3st17pdcq22ge">
                  <Link to="/place" className="decoration-olive decoration-1 underline-offset-[7px] after:absolute after:inset-0 after:content-[''] group-hover:underline" data-id="element-680" data-mp="1m2l9u281tesq">
                    {type.name}
                  </Link>
                </h3>
                <p className="mt-3 max-w-sm text-[14.5px] leading-[1.8] text-muted" data-id="element-681" data-mp="1w6i2p0h0thbn">
                  {type.blurb}
                </p>
                <dl className="mt-6 grid grid-cols-3 gap-x-4 border-t border-line pt-4" data-id="element-682" data-mp="29yyasfos45jf">
                  {[['Programs', String(type.programs)], ['Ages', type.ages], ['Sign-ups', type.signUps]].map(([term, value]) => <div key={term} data-id="element-683" data-mp="2ew7y9psly74m">
                      <dt className="label text-muted/70" data-id="element-684" data-mp="2ezdoj8cz3f1x">{term}</dt>
                      <dd className="mt-1.5 text-[13px] leading-snug text-ink" data-id="element-685" data-mp="2sw8z558vhek3">
                        {value}
                      </dd>
                    </div>)}
                </dl>
              </li>)}
          </ul>

          <div className="mt-16 max-w-3xl border-l-2 border-olive pl-8" data-id="element-686" data-mp="uhfqnq0jgkn7">
            <DesignNote label="Three levels, not two" note="Hub → activity type page → provider page. Only a handful of providers ever earn that third level." data-id="element-687" data-mp="uj92rktx7x2l" />
            <p className="text-[15.5px] leading-[1.85] text-ink/80" data-id="element-688" data-mp="10ziui0ft69gc">
              Yes — keep the pages you already have for each type of class. They
              are the strongest asset in this cluster and they should get
              longer, not fewer. Individual studios stay as rows inside them
              until one earns its own page: it has a waitlist people ask you
              about, or it wants to advertise.
            </p>
          </div>
        </div>
      </section>

      <FilterBar filters={classesFilters} active={active} onToggle={toggle} data-id="element-689" data-mp="m77cfeyxqdl6" />

      <section aria-labelledby="programs-h" className="bg-white" data-id="element-690" data-mp="r00etw6xl9u3">
        <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-691" data-mp="3pps7nkctn4wr">
          <TierNote tier="2" label="Provider rows" note="These sit on the activity type page too. Same row, written once, shown in both places." data-id="element-692" data-mp="3uobvjqaz6qrr" />
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-693" data-mp="3n2gdfu3ue6iw">
            <h2 id="programs-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-694" data-mp="3i6ixvq0el476">
              The programs parents ask about most
            </h2>
            <p className="label text-muted" role="status" data-id="element-695" data-mp="345yht1lvhmhd">
              {filtered.length} {filtered.length === 1 ? 'program' : 'programs'}
              {active.length > 0 && ` · ${active.length} filters`}
            </p>
          </div>

          {filtered.length === 0 ? <div className="mt-14 max-w-lg" data-id="element-696" data-mp="2ue2hrl087mai">
              <h3 className="font-display text-[30px] font-300 text-ink" data-id="element-697" data-mp="2phtv7mjp8qtb">
                Nothing matches all of that
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-698" data-mp="2lenpqh4ev3nw">
                Try dropping the season filter — most programs run year-round.
              </p>
              <button type="button" onClick={() => setActive([])} className="label mt-8 bg-ink px-7 py-3.5 text-paper transition-colors duration-150 ease-soft hover:bg-olive" data-id="element-699" data-mp="26rozuql7chba">
                Clear filters
              </button>
            </div> : <ul className="mt-12 border-t border-line" data-id="element-700" data-mp="21txao4bxz9as">
              {filtered.map(program => <li key={program.id} data-id="element-701" data-mp="2pk1cq79hf0zo">
                  <article className="grid gap-y-5 border-b border-line py-9 lg:grid-cols-[1.6fr_1fr] lg:gap-x-16" data-id="element-702" data-mp="2ui2xcwmk38d9">
                    <div data-id="element-703" data-mp="2fu82b9l2srx0">
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1" data-id="element-704" data-mp="2fsn499zjyque">
                        <h3 className="font-display text-[28px] font-300 leading-tight text-ink" data-id="element-705" data-mp="2krhq6kstgviq">
                          {program.name}
                        </h3>
                        <span className="label text-olive" data-id="element-706" data-mp="3d8kc1xak03fm">
                          {program.activity} · {program.area}
                        </span>
                      </div>
                      <p className="mt-4 max-w-prose text-[15.5px] leading-[1.8] text-muted" data-id="element-707" data-mp="11rigfp2kiz7z">
                        {program.blurb}
                      </p>
                      <a href="#faq" className="label mt-5 inline-flex items-center gap-1.5 text-ink underline decoration-olive underline-offset-4" data-id="element-708" data-mp="1akvjhjucjh7s">
                        Program website
                        <ArrowUpRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-709" data-mp="dwfq1co48npe" />
                      </a>
                    </div>
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 self-start lg:grid-cols-2" data-id="element-710" data-mp="rwgoymktll28">
                      {[['Ages', program.ages], ['Price', program.price], ['Season', program.session], ['Type', program.activity]].map(([term, value]) => <div key={term} data-id="element-711" data-mp="2ienagr52hyhs">
                          <dt className="label text-muted/70" data-id="element-712" data-mp="2ifoxltzcybfv">{term}</dt>
                          <dd className="mt-1.5 text-[14px] leading-snug text-ink" data-id="element-713" data-mp="2ne05w0a08dvz">
                            {value}
                          </dd>
                        </div>)}
                    </dl>
                  </article>
                </li>)}
            </ul>}
        </div>
      </section>

      <DirectoryColumns title="Every other program in the county" groups={classesTier3} data-id="element-714" data-mp="316ptg956lqv7" />
      <HubFaqs faqs={classesFaqs} data-id="element-715" data-mp="3b2jekgwyusol" />
      <SiblingHubs exclude={['Classes & sports']} data-id="element-716" data-mp="3b3ktwxl0m1c4" />
    </main>;
}
function OpenNowStrip() {
  return <section aria-labelledby="open-h" className="border-y border-line bg-foam/40" data-id="element-717" data-mp="3f2tna1c57x75">
      <div className="mx-auto max-w-shell px-6 py-12 lg:px-10" data-id="element-718" data-mp="1gpa0scxe0l0j">
        <DesignNote label="The reason people come back" note="Registration windows are the one thing no other Santa Barbara site tracks. Put them above everything else and this page earns repeat visits." data-id="element-719" data-mp="1kq3tdjlrynzr" />
        <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-720" data-mp="18fmayvfup5y2">
          <h2 id="open-h" className="font-display text-[32px] font-300 leading-tight text-ink sm:text-[38px]" data-id="element-721" data-mp="13ghynvxmqs3i">
            Registration open right now
          </h2>
          <Link to="/events" className="label border-b border-line pb-1.5 text-ink transition-colors duration-150 ease-soft hover:border-olive" data-id="element-722" data-mp="1w56rl7oj7r46">
            Get the reminder email
          </Link>
        </div>

        <ul className="mt-9 grid gap-x-14 gap-y-6 sm:grid-cols-3" data-id="element-723" data-mp="1n3790axvcez4">
          {openNow.map(item => <li key={item.program} className="flex items-start gap-4" data-id="element-724" data-mp="1i55ve5j7p7v1">
              <span className={`label mt-1 shrink-0 px-2.5 py-1 ${urgencyStyles[item.urgency]}`} data-id="element-725" data-mp="bqmy134bnfzj">
                {item.closes}
              </span>
              <span data-id="element-726" data-mp="1r3i49wvdt7co">
                <span className="block font-display text-[21px] font-400 leading-snug text-ink" data-id="element-727" data-mp="1r52zlopisubl">
                  {item.program}
                </span>
                <span className="mt-1 block text-[12.5px] text-muted" data-id="element-728" data-mp="1zwbng5dy8qvt">
                  {item.activity}
                </span>
              </span>
            </li>)}
        </ul>
      </div>
    </section>;
}